import React, { useState } from 'react';
import {
	Link as RouterLink,
	useLocation,
	useNavigate,
	useParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
	BarChart,
	BarChartFill,
	CardText,
	Archive,
	ArchiveFill,
} from 'react-bootstrap-icons';
import { useTheme } from '@mui/styles';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import ListItemIcon from '@mui/material/ListItemIcon';
import isUndefined from 'lodash/isUndefined';
import Logo from '../../assets/Logo';
import SearchInput from '../SearchInput';
import AccountAnchor from '../AccountAnchor';
import { useSearchApi } from '../../apis';
import { parseApiResponseData } from '../../apis/Utilities/parseApiResponseData';

const drawerWidth = '18.75rem';
function SidebarMenu({ ordersCount, productsCount, ...props }) {
	const theme = useTheme();
	const { t } = useTranslation('sidebar', 'common');
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const { getCounts } = useSearchApi();
	const [mobileOpen, setMobileOpen] = useState(false);
	const { window } = props;
	const { searchKey } = useParams();
	const [hoverIcon, setHoverIcon] = useState([]);
	const dashboardPath = '/';
	const ordersPath = `/search/${searchKey}/orders`;
	const productsPath = `/search/${searchKey}/products`;

	function handleHover(i, isLeaving) {
		setHoverIcon((prevItems) => {
			if (isLeaving) return prevItems.filter((item) => item !== i);
			return [...prevItems, i];
		});
	}

	const navLinks = [
		{
			label: 'dashboard',
			href: dashboardPath,
			count: undefined,
			icon: <BarChart />,
			iconHover: <BarChartFill />,
		},
		{
			label: 'orders',
			href: ordersCount >= 0 ? ordersPath : '/orders',
			count: ordersCount,
			icon: <CardText />,
			iconHover: <CardText />,
		},
		{
			label: 'products',
			href: productsCount >= 0 ? productsPath : '/products',
			count: productsCount,
			icon: <Archive />,
			iconHover: <ArchiveFill />,
		},
	];

	const handleDrawerToggle = () => {
		setMobileOpen(!mobileOpen);
	};

	const handleSearch = async (searchTerm) => {
		const trimmedSearch = searchTerm.trim();
		const isSearchRoute = /^\/search\/.+\/(orders|products)(\/|$)/.test(
			pathname
		);
		const isProductsRoute = /products(\/|$)/.test(pathname);

		if (trimmedSearch === '') {
			navigate(isProductsRoute ? '/products' : '/orders');
			return;
		}

		if (isSearchRoute) {
			navigate(
				`/search/${trimmedSearch}${isProductsRoute ? '/products' : '/orders'}`
			);
			return;
		}

		try {
			const response = await getCounts({
				searchTerm: trimmedSearch,
			});

			if (response?.isAborted) {
				return;
			}

			if (response?.isError) {
				navigate(`/search/${trimmedSearch}/orders`);
				return;
			}

			const data = parseApiResponseData(response, {});
			const ordersMatchCount = Number(data?.Count_Orders || 0);
			const productsMatchCount = Number(data?.Count_Products || 0);

			if (ordersMatchCount === 0 && productsMatchCount === 0) {
				navigate(
					`/search/${trimmedSearch}${isProductsRoute ? '/products' : '/orders'}`
				);
				return;
			}

			if (ordersMatchCount === 0 && productsMatchCount > 0) {
				navigate(`/search/${trimmedSearch}/products`);
				return;
			}

			navigate(`/search/${trimmedSearch}/orders`);
		} catch (error) {
			console.error(error);
		}
	};

	const drawer = (
		<div>
			<Toolbar sx={{ mt: { xs: 1.75 }, ml: { sm: '-1rem' } }}>
				<RouterLink to="/" aria-label="Smurfit Westrock logo">
					<Logo />
				</RouterLink>
			</Toolbar>
			<Toolbar
				sx={{
					display: { xs: 'none', sm: 'block' },
					mt: theme.spacing(1.25),
					mb: theme.spacing(-4.65),
					ml: { sm: '-.35rem' },
				}}
			>
				<Typography
					sx={{
						color: theme.palette.white.colorWhite,
						textSize: theme.palette.textSize.sizeMd,
					}}
				>
					{t('app_name')}
				</Typography>
			</Toolbar>
			<Toolbar
				sx={{
					display: { sm: 'block', md: 'none' },
					mt: { xs: 5, sm: 1 },
					mb: { xs: 2, sm: 0 },
					ml: { sm: '.1rem' },
				}}
			>
				<SearchInput onSearch={handleSearch} />
			</Toolbar>
			{navLinks.map((navLink, i) => (
				<ListItem
					key={navLink.href}
					to={navLink.href}
					component={RouterLink}
					disablePadding
					onClick={() => handleHover(i, true)}
				>
					<ListItemButton selected={pathname === navLink.href}>
						<ListItemIcon
							sx={{
								ml: { sm: '.1rem' },
							}}
						>
							{hoverIcon.includes(i) ? navLink.iconHover : navLink.icon}
						</ListItemIcon>
						<ListItemText
							primary={`${t(`${navLink.label}`)} ${
								!isUndefined(navLink.count) ? `(${navLink.count})` : ''
							}`}
							primaryTypographyProps={{
								fontSize: theme.palette.textSize.sizeMd,
								fontWeight: hoverIcon.includes(i)
									? theme.palette.font.weightBold
									: theme.palette.font.weightRegular,
							}}
							sx={{
								ml: { xs: '-1.65rem', sm: '-1.4rem' },
								mt: { xs: '.4rem', sm: '.55rem' },
							}}
						/>
					</ListItemButton>
				</ListItem>
			))}
		</div>
	);

	const container =
		window !== undefined ? () => window().document.body : undefined;

	return (
		<>
			<CssBaseline />
			<AccountAnchor
				onSearch={handleSearch}
				handleOpenDrawer={handleDrawerToggle}
			/>
			<Box
				component="nav"
				sx={{ width: { md: drawerWidth }, flexShrink: { sm: 0 } }}
				aria-label="menu"
			>
				<Drawer
					container={container}
					variant="temporary"
					open={mobileOpen}
					onClose={handleDrawerToggle}
					ModalProps={{
						keepMounted: true,
					}}
					sx={{
						display: { xs: 'block', sm: 'block', md: 'none' },
						'& .MuiDrawer-paper': {
							boxSizing: 'border-box',
							width: drawerWidth,
							backgroundColor: theme.palette.navy.colorNavy90,
							color: theme.palette.white.colorWhite,
						},
					}}
				>
					{drawer}
				</Drawer>
				<Drawer
					variant="permanent"
					sx={{
						display: { xs: 'none', sm: 'none', md: 'block' },
						'& .MuiDrawer-paper': {
							boxSizing: 'border-box',
							width: drawerWidth,
							backgroundColor: theme.palette.navy.colorNavy90,
							color: theme.palette.white.colorWhite,
						},
					}}
					open
				>
					{drawer}
				</Drawer>
			</Box>
		</>
	);
}

SidebarMenu.propTypes = {
	window: PropTypes.func,
	ordersCount: PropTypes.number,
	productsCount: PropTypes.number,
};

export default SidebarMenu;
